import isNil from "lodash/isNil";
import { useActivityShowContext } from "PFApp/activities/show/context/activity_show_context";
import { useActivityMatches } from "PFApp/activities/show/hooks/use_activity_matches";
import { useCurrentActivity } from "PFApp/activities/show/hooks/use_current_activity";
import RefreshAction from "PFApp/activities/show/parts/refresh_action";
import { useMatchesActions } from "PFApp/activities/show/store/matches";
import { ProfilesListMode } from "PFApp/activities/types/profiles_view_mode";
import { CvExportsButton } from "PFApp/components/cv_exports_button";
import { FiltersV2TermSearch, useFiltersState, useFiltersV2Actions } from "PFApp/components/filters_v2";
import { SearchEntity } from "PFApp/components/search_autocomplete/search_autocomplete.types";
import { TablePropertiesButton } from "PFApp/components/table_properties/table_properties_button";
import { TablePropertiesSidePanel } from "PFApp/components/table_properties/table_properties_side_panel";
import { Alert } from "PFComponents/alert";
import { useShortlistsInvalidate } from "PFCore/hooks/queries/shortlists/use_shortlists_invalidate";
import { useIsUnbiasedMode } from "PFCore/hooks/use_is_unbiased_mode";
import { RecentSearchesPageKey } from "PFTypes/recent_searches";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { OrderSelector } from "../../components/order_selector";
import { ProfileViewModeToggle } from "../../components/profiles_view_mode_toggle";
import css from "./matches_header.module.scss";

export const MatchesHeader = () => {
  const { t } = useTranslation(["activities", "translation", "filters"]);

  const activity = useCurrentActivity();
  const isUnbiasedMode = useIsUnbiasedMode(activity?.reviewers);
  const {
    availableProfilesListColumns,
    selectedProfilesListColumns,
    profilesListMode,
    setSelectedProfilesListColumns
  } = useActivityShowContext();

  const { matches, matchesMeta } = useActivityMatches();
  const { updateMatchesParams } = useMatchesActions();

  const { id, coowned, state } = activity;
  const { invalidateShortlists } = useShortlistsInvalidate();
  const [isTablePropertiesVisible, setTablePropertiesVisibility] = useState(false);

  useEffect(() => {
    if (profilesListMode === ProfilesListMode.Cards) {
      setTablePropertiesVisibility(false);
    }
  }, [profilesListMode, setTablePropertiesVisibility]);

  const isCompleted = state === "complete";

  const handleOrderChange = (value) => updateMatchesParams({ order: value, page: 1 });

  const { areFiltersModified, isAnyFilterSelected } = useFiltersState();
  const { resetValues } = useFiltersV2Actions();

  const paginationLabel = useMemo(() => {
    const { page, perPage, total } = matchesMeta || {};

    if (total === 0 || isNil(total)) {
      return null;
    }
    const start = (page! - 1) * perPage! + 1;
    const end = Math.min(page! * perPage!, total);

    return t(
      `activities:show.matches.${
        isAnyFilterSelected ? "matchesPaginationInfo" : "matchesPaginationInfoFiltered"
      }`,
      { start, end, count: total }
    );
  }, [isAnyFilterSelected, matchesMeta, t]);

  return (
    <>
      <div className={css.container}>
        <FiltersV2TermSearch
          pageKey={RecentSearchesPageKey.ActivityMatches}
          autocompleteConfig={[
            {
              entity: SearchEntity.Profile,
              useAsTerm: true
            }
          ]}
          includeRecentSearches={false}
          qaId="ActivityMatchesTabSearchInput"
          narrow
          disabled={isUnbiasedMode}
        />
        <OrderSelector
          meta={matchesMeta!}
          onOrderChange={handleOrderChange}
          showSelect={!isCompleted}
          label={paginationLabel}
          className={css.sortByBlock}
        />
        <ProfileViewModeToggle />
        {profilesListMode === ProfilesListMode.Table && (
          <TablePropertiesButton onClick={() => setTablePropertiesVisibility(true)} />
        )}
        <CvExportsButton
          profileIds={(matches || []).map(({ profile }) => profile.id)}
          activityId={id}
          qaId="ActivityMatchesTabExportButton"
        />
        {!isCompleted && coowned && (
          <RefreshAction
            activityId={id}
            onUpdate={() => invalidateShortlists(id)}
            showLabel={false}
            qaId="ActivityMatchesTabRefreshMatchesButton"
          />
        )}
      </div>
      {areFiltersModified && (
        <Alert
          title={t("activities:show.matches.filtersChangedWarning")}
          action={{
            text: t("filters:restoreAll"),
            icon: "history",
            kind: "text",
            onClick: () => resetValues()
          }}
        />
      )}
      <TablePropertiesSidePanel
        isVisible={isTablePropertiesVisible}
        onCloseClick={() => setTablePropertiesVisibility(false)}
        availableColumns={availableProfilesListColumns}
        selectedColumns={selectedProfilesListColumns}
        onColumnsChange={setSelectedProfilesListColumns}
      />
    </>
  );
};
